<template>
  <flow-layout :current-step-index="isBrokerFlow ? 5 : 4">
    <heading level="2">{{
      isBrokerFlow ? $t('statementCollect.brokerTitle') : $t('statementCollect.title')
    }}</heading>
    <div class="bankStatementDocumentId" v-if="shouldShowBankStatementDocumentId">
      <input-inline>
        <input-text
          id="bank-statement-document-id"
          name="bank-statement-document-id"
          v-model="application.brokerflowCode"
          @input="checkFormValidation"
          :strict-mode="{ regex: /\./g }"
          :maxlength="BANK_STATEMENT_DOCUMENT_ID"
          v-validation="[['dynamic', '/brokerflowCode']]"
        >
          <template #label>
            {{ t('bankStatementDocumentId.label') }}
            <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
              <i18n
                :path="
                  isBrokerFlow
                    ? 'statementCollect.bankStatementDocumentId.brokerToolTip'
                    : 'statementCollect.bankStatementDocumentId.toolTip'
                "
              ></i18n>
            </popover>
          </template>
        </input-text>
      </input-inline>
      <p v-if="isBrokerFlow" class="bankStatementDocumentIdHint">
        {{ t('bankStatementDocumentId.hint') }}
      </p>
    </div>
    <div class="overdraftFacilityQuestion">
      <input-radio-group
        id="overdraft-facility"
        v-model="application._extension.haveOverdraftFacility"
        :options="overdraftFacilityOptions"
        v-validation.noEvent="['requiredBoolean']"
        @input="checkFormValidation"
      >
        <template #legend>{{
          isBrokerFlow ? t('overdraftFacility.brokerLabel') : t('overdraftFacility.label')
        }}</template>
      </input-radio-group>
    </div>
    <div class="overdraftLimitAmount" v-if="showOverdraftLimit">
      <input-inline>
        <input-money
          id="overdraft-limit"
          name="overdraft-limit"
          v-model="application.overdraftLimit"
          @input="checkFormValidation"
          :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
          :maxlength="OVERDRAFT_LIMIT_MAX_LENGTH"
          v-validation="[['dynamic', '/overdraftLimit']]"
        >
          <template #label>
            {{ t('overdraftLimit.label') }}
            <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
              <i18n
                :path="
                  isBrokerFlow
                    ? 'statementCollect.overdraftLimit.brokerToolTip'
                    : 'statementCollect.overdraftLimit.toolTip'
                "
                class="tooltip-text"
              ></i18n>
            </popover>
          </template>
          <template #prefix>{{ $t('currency.dollar') }}</template>
        </input-money>
      </input-inline>
      <p class="overdraftLimitHint">
        {{ isBrokerFlow ? t('overdraftFacility.brokerHint') : t('overdraftFacility.hint') }}
      </p>
    </div>
    <illion-statement-uploader
      v-if="!isBrokerFlow"
      :application="application"
      :updateApplicationWithStore="updateApplicationWithStore"
      @electronicUploadSuccessful="onElectronicUploadSuccessful"
    >
      <template #illion-statement-uploader-online-tab-content>
        <div class="electronicVerification">
          <faq-modal v-if="showFAQModal" @close="triggerModal" />
          <p class="heading">{{ t('electronicVerification.title') }}</p>
          <p class="description" v-for="(simpleStep, index) in simpleSteps" :key="index">
            {{ simpleStep }}
          </p>
          <div class="hintBox">
            <brand-link @click="triggerModal" class="hint">
              <span class="hintText">{{ t('electronicVerification.questions') }}</span>
            </brand-link>
          </div>
        </div>
      </template>

      <template #illion-flow-consents>
        <consents
          :pageConsents="bankStatementConsents"
          :consentsPath="consentsPath"
          v-model="application.consents"
        />
      </template>
    </illion-statement-uploader>

    <navigation-buttons
      @back="goBack"
      @next="onSubmit"
      :next="{
        label: $t('nextButtonLabel'),
        modifiers: { disabled: disableButtonNext || !isFormValid },
      }"
    />
  </flow-layout>
</template>
<script>
import { mapGetters } from 'vuex';
import { compare } from 'fast-json-patch/index.mjs';
import FlowLayout from '@/views/layouts/FlowLayout';
import FaqModal from '@/components/FaqModal';
import IllionStatementUploader from '@/components/illionComponents/IllionStatementUploader';
import Consents from '@/components/Consents';

import applicationMixin from '@/mixins/application';
import brokerFlowMixin from '@/mixins/broker-flow';
import uploadFilesMixin from '@/mixins/upload-files';
import bankStatementConsents from '@/modules/bank-statement-consents';
import { ILLION_PRE_SUBMIT_ADR, ILLION_POST_SUBMIT_ADR } from '@/modules/illion-adrs';
import * as fileStates from '@/modules/upload-service/file-states';
import buildTranslators from '@/utils/build-translators';
import mapConstsToOptions from '@/utils/map-consts-to-options';
import {
  OVERDRAFT_LIMIT_MAX_LENGTH,
  BANK_STATEMENT_DOCUMENT_ID,
} from '@/modules/validation-service/fields-length';
import InputInline from '@/components/InputInline';
import InputMoney from '@/components/InputMoney';
import InputRadioGroup from '@/components/InputRadioGroup';

export default {
  name: 'statement-collect',
  components: {
    FlowLayout,
    FaqModal,
    IllionStatementUploader,
    Consents,
    InputInline,
    InputMoney,
    InputRadioGroup,
  },
  data() {
    return {
      showFAQModal: false,
      simpleSteps: [
        this.t('electronicVerification.description.firstStep'),
        this.t('electronicVerification.description.secondStep'),
        this.t('electronicVerification.description.thirdStep'),
      ],
      bankStatementConsents,
      electronicUploadSuccessful: false,
      isFormValid: false,
    };
  },
  computed: {
    ...mapGetters({
      allFiles: 'file/getAll',
      isUploadInProgress: 'file/isUploadInProgress',
    }),
    consentsPath() {
      return 'statementCollect.electronicVerification.consents';
    },
    uploadedFiles() {
      return this.allFiles.filter(file => file.isUploaded);
    },
    errorFiles() {
      return this.allFiles.filter(
        file =>
          file.documentUploadStatus === fileStates.ERROR ||
          file.documentUploadStatus === fileStates.DELETE_ERROR,
      );
    },
    disableButtonNext() {
      const haveOverdraft = this.application._extension.haveOverdraftFacility;

      // Radio must be answered
      if (haveOverdraft === null || haveOverdraft === undefined) {
        return true;
      }

      // Only validate overdraft limit when "Yes" is selected
      const invalidOverdraft =
        haveOverdraft === true &&
        (!this.application.overdraftLimit ||
          !!this.$validations?.collections?.find(v => v.key === 'overdraft-limit')?.value
            ?.errorMessage);

      const documentIdValidation = this.$validations?.collections?.find(
        v => v.key === 'bank-statement-document-id',
      );

      const hasValidUploadMethod =
        this.someBankStatementFilesAreUploaded ||
        this.electronicUploadSuccessful ||
        (this.shouldShowBankStatementDocumentId &&
          this.application.brokerflowCode &&
          !documentIdValidation?.value?.errorMessage);

      const isNotValid =
        invalidOverdraft ||
        this.isUploadInProgress ||
        (this.fileUploadServiceError && !this.someBankStatementFilesAreUploaded) ||
        !hasValidUploadMethod;

      return isNotValid;
    },
    fileUploadServiceError() {
      return !!this.errorFiles.length;
    },
    someBankStatementFilesAreUploaded() {
      return this.uploadedFiles.some(file =>
        [ILLION_PRE_SUBMIT_ADR, ILLION_POST_SUBMIT_ADR].includes(file.adr?.template?.name),
      );
    },
    OVERDRAFT_LIMIT_MAX_LENGTH() {
      return OVERDRAFT_LIMIT_MAX_LENGTH;
    },
    BANK_STATEMENT_DOCUMENT_ID() {
      return BANK_STATEMENT_DOCUMENT_ID;
    },
    primaryOwner() {
      return this.application?.owners?.[0] || {};
    },
    shouldShowBankStatementDocumentId() {
      return (
        (this.primaryOwner._extension?.ondeckBroker || this.isBrokerFlow) &&
        !this.someBankStatementFilesAreUploaded &&
        !this.electronicUploadSuccessful
      );
    },
    overdraftFacilityOptions() {
      return mapConstsToOptions([true, false], 'statementCollect.overdraftFacility.options');
    },
    showOverdraftLimit() {
      return this.application._extension.haveOverdraftFacility === true;
    },
  },
  watch: {
    'application._extension.haveOverdraftFacility'(newVal) {
      if (newVal === false && this.application.overdraftLimit) {
        this.application.overdraftLimit = null;
      }
      this.checkFormValidation();
    },
    allFiles() {
      this.checkFormValidation();
    },
  },
  mixins: [applicationMixin, uploadFilesMixin, brokerFlowMixin],
  methods: {
    async onSubmit() {
      this.application._extension.isStatementsManuallyUploaded = this.someBankStatementFilesAreUploaded;
      await this.submit();
    },
    generatePatch() {
      const patch = compare(
        this.$store.getters['application/getPiiApplication'](),
        this.application,
      );
      if (this.electronicUploadSuccessful) {
        return patch.filter(op => op.path !== '/brokerflowCode');
      }
      return patch;
    },
    triggerModal() {
      this.showFAQModal = !this.showFAQModal;
    },
    t(key, options) {
      return buildTranslators('statementCollect').t(key, options);
    },
    onElectronicUploadSuccessful() {
      this.electronicUploadSuccessful = true;
      this.checkFormValidation();
    },
    checkFormValidation() {
      this.$nextTick().then(() => {
        this.isFormValidFn();
      });
    },
    isFormValidFn() {
      let validCount = 0;
      const fields = this.$validations.collections[0];
      this.isFormValid = false;

      fields.forEach((value, key) => {
        const { component } = value;
        const hasValue =
          component.value !== null && component.value !== undefined && component.value !== '';
        const isTextValid = hasValue && component.errorMessage === '';
        const isCheckboxValid = key.includes('agreement-checkbox') && component.checked;
        if (isTextValid || isCheckboxValid) {
          validCount += 1;
        }
      });
      this.isFormValid = validCount === fields.size;
    },
  },
};
</script>
<style lang="sass" scoped>
.overdraftFacilityQuestion
  margin: $glass-spacing-3 0 $glass-spacing-2 0

.overdraftLimitAmount
  margin-bottom: $glass-spacing-6

.overdraftLimitHint
  +font-size(13.33, 20)
  color: $color-system-neutral-gray-5
  font-style: italic
  margin-top: $glass-spacing-1

.bankStatementDocumentId
  margin: $glass-spacing-4 0 $glass-spacing-4 0

.bankStatementDocumentIdHint
  +font-size(13.33, 20)
  color: $color-system-neutral-gray-5
  font-style: italic
  margin-top: $glass-spacing-1

.electronicVerification
  width: 100%
  display: flex
  flex-direction: column
  justify-content: center
  color: $ebs-text-color

  .heading
    +font-size(16, 20)
    font-weight: bold
    line-height: $glass-spacing-3
    margin-top: 0

  .description
    +font-size(13.33, 20)
    margin: 0
    margin-top: $glass-spacing-05
    color: $color-system-neutral-gray-5

  .hintBox
    border-bottom: 1px solid $color-system-neutral-gray-3
    padding-bottom: $glass-spacing-105
    margin: $glass-spacing-2 0 $glass-spacing-2

  .hint
    +font-size(13.33, 16)
    cursor: pointer

    .hintText
      color: $brand-link-partner-color
      text-decoration: underline;
      font-weight: bold

.tooltip-text
  white-space: pre-line

.popover
  vertical-align: super
  ::v-deep
    .icon
      color: $glass-color-brand-primary-dark-03
    .content
      background: $glass-color-brand-secondary-accent-01
      color: #000000
      z-index: 2
    .iconButton
      &:focus
        outline: 0
        box-shadow: 0 0 0 4px $glass-color-brand-primary-light-02

::v-deep
  .brandButton.inputFile
    color: $glass-color-brand-primary-dark-03
    border-color: $glass-color-brand-primary-dark-03

  .tabsButton
    color: $color-system-neutral-black
    &[selected]
      --button-background-color: #0074ff
      --button-border-color: #0074ff
      color: #ffffff
</style>
